/**
 * CSV, written in the browser.
 *
 * What an operator wants from an export is the rows they are looking at, right now, with
 * the headers the table shows. That is a Blob and a click, not a server job and a reference
 * number to go and find later.
 *
 * Two screens now export, so the quoting and the download live here rather than in one of
 * them. Nothing in this file recomputes a figure: dividing cents by 100 for the spreadsheet
 * is the whole of the arithmetic.
 */

/**
 * RFC 4180 quoting, plus formula neutralisation.
 *
 * A customer name with a comma in it is not a reason to corrupt a file, and a name that
 * begins `=`, `+`, `-`, `@`, tab or carriage return is not a reason to hand Excel a
 * formula. Names, SKUs, payer names and order notes all come from outside, which means
 * they are attacker reachable, so the leading character is escaped with an apostrophe the
 * way OWASP describes. Nothing else about the value changes.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

export function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  const text = typeof value === 'string' && FORMULA_LEAD.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Header row plus body rows, CRLF separated, with the trailing newline tools expect. */
export function toCsv(
  headers: readonly string[],
  rows: readonly (readonly (string | number | null | undefined)[])[],
): string {
  const lines = [headers.map(csvCell).join(',')];
  for (const row of rows) lines.push(row.map(csvCell).join(','));
  return `${lines.join('\r\n')}\r\n`;
}

export function dollars(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? '' : (cents / 100).toFixed(2);
}

/**
 * Hands the file to the browser. Returns false when the environment has no object URL
 * support, for example jsdom, so a caller can tell the difference between "did not run"
 * and "ran and downloaded nothing".
 */
export function downloadCsv(filename: string, csv: string): boolean {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return false;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick: revoking synchronously races the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}
